import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { OpportunityCard } from "../directory/OpportunityCard";

interface RecruiterOpportunitiesProps {
  profileId: string;
}

export const RecruiterOpportunities = ({ profileId }: RecruiterOpportunitiesProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: opportunities, isLoading } = useQuery({
    queryKey: ["recruiter-opportunities", profileId],
    queryFn: async () => {
      console.log("Fetching opportunities for recruiter:", profileId);
      const { data, error } = await supabase
        .from("opportunities")
        .select(`
          *,
          profiles:created_by (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq("created_by", profileId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching opportunities:", error);
        throw error;
      }

      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-48 bg-muted rounded-lg"></div>
      </div>
    );
  }

  if (!opportunities?.length) {
    return (
      <Card className="p-8 text-center space-y-4 backdrop-blur-sm bg-background/80 border-2 border-muted">
        <h3 className="text-xl font-semibold text-muted-foreground">
          {t('profile.noOpportunities')}
        </h3>
        <p className="text-muted-foreground">
          {t('profile.createFirstOpportunity')}
        </p>
        <Button
          onClick={() => navigate('/opportunities/create')}
          className="mt-4"
          size="lg"
        >
          <PlusCircle className="mr-2 h-5 w-5" />
          {t('profile.createOpportunity')}
        </Button>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
      {opportunities.map((opportunity) => (
        <OpportunityCard
          key={opportunity.id}
          id={opportunity.id}
          title={opportunity.title}
          description={opportunity.description}
          type={opportunity.type}
          location={opportunity.location}
          remoteAllowed={opportunity.remote_allowed || false}
          compensation={opportunity.compensation}
          deadline={opportunity.deadline}
          requirements={opportunity.requirements}
          lookingFor={opportunity.looking_for}
          createdBy={opportunity.profiles ? {
            id: opportunity.profiles.id,
            fullName: opportunity.profiles.full_name,
            avatarUrl: opportunity.profiles.avatar_url,
          } : null}
        />
      ))}
    </div>
  );
};